import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isPaid, type Vendor } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { TextInput } from '@/components/ui/Field';
import { money } from '@/lib/format';
import { PaymentTotals } from './PaymentTotals';
import { ApiRequestError } from '@/lib/api';
import { useAddPayment, useDeletePayment, useUpdatePayment } from './queries';

/**
 * The instalments owed to one vendor.
 *
 * Marking something paid is one click and records today's date, because that is
 * what happens: you pay, then you tell the app. Editing the date afterwards is
 * for correcting it, not for the normal case.
 *
 * Each row carries the vendor's `updatedAt` as its concurrency token — the
 * schedule lives on the vendor row, so two people editing different instalments
 * at once is the same write, and the 409 is what stops one of them vanishing.
 */
export function PaymentSchedule({
  wedId,
  vendor,
  currency,
}: {
  wedId: string;
  vendor: Vendor;
  currency: string;
}) {
  const { t } = useTranslation();
  const add = useAddPayment(wedId);
  const update = useUpdatePayment(wedId);
  const remove = useDeletePayment(wedId);

  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string>();

  const payments = vendor.payments ?? [];
  const money_ = (cents: number) => money(cents, vendor.currency ?? currency);

  const handle = async (run: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await run();
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.code === 'version_conflict'
            ? t('vendors.conflict')
            : err.message
          : t('common.somethingWrong'),
      );
    }
  };

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!label.trim() || !amount) return;
    await handle(async () => {
      await add.mutateAsync({
        vendorId: vendor.vendorId,
        input: {
          label: label.trim(),
          amountCents: Math.round(Number(amount) * 100),
          ...(dueDate ? { dueDate } : {}),
          updatedAt: vendor.updatedAt,
        },
      });
      setLabel('');
      setAmount('');
      setDueDate('');
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-section">{t('vendors.schedule')}</h3>

      {/* Above the list, not below it: the totals are the answer, the rows are
          the working. */}
      <PaymentTotals vendor={vendor} currency={currency} />

      {payments.length === 0 ? (
        <p className="text-caption text-leaf-soft">{t('vendors.noPayments')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {payments.map((p) => (
            <li key={p.paymentId} className="flex flex-wrap items-center gap-2 py-2">
              <span className="flex min-w-[9rem] flex-1 flex-col">
                <span className="text-body">{p.label}</span>
                <span className="text-caption text-leaf-soft">
                  {p.dueDate ? p.dueDate : t('vendors.noDate')}
                </span>
              </span>

              <span className="tabular text-body">{money_(p.amountCents)}</span>

              {isPaid(p) ? (
                <span className="flex items-center gap-2">
                  <Pill tone="attending">{t('vendors.paidOn', { date: p.paidAt })}</Pill>
                  <button
                    type="button"
                    onClick={() =>
                      void handle(() =>
                        update.mutateAsync({
                          vendorId: vendor.vendorId,
                          paymentId: p.paymentId,
                          // Undefined clears it: the schema drops the key rather
                          // than storing an empty date.
                          input: { paidAt: undefined, updatedAt: vendor.updatedAt },
                        }),
                      )
                    }
                    className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
                  >
                    {t('vendors.markUnpaid')}
                  </button>
                </span>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void handle(() =>
                      update.mutateAsync({
                        vendorId: vendor.vendorId,
                        paymentId: p.paymentId,
                        input: {
                          paidAt: new Date().toISOString().slice(0, 10),
                          updatedAt: vendor.updatedAt,
                        },
                      }),
                    )
                  }
                >
                  {t('vendors.markPaid')}
                </Button>
              )}

              {/* A paid instalment cannot be removed until it is unmarked —
                  deleting it would quietly reduce the paid total. */}
              {!isPaid(p) && (
                <button
                  type="button"
                  onClick={() =>
                    void handle(() =>
                      remove.mutateAsync({ vendorId: vendor.vendorId, paymentId: p.paymentId }),
                    )
                  }
                  className="min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                >
                  {t('common.remove')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="text-body text-status-declinedFg">
          {error}
        </p>
      )}

      <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => void onAdd(e)}>
        <label className="min-w-[8rem] flex-1">
          <span className="sr-only">{t('vendors.paymentLabel')}</span>
          <TextInput
            placeholder={t('vendors.paymentLabel')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className="w-28">
          <span className="sr-only">{t('vendors.amount')}</span>
          <TextInput
            type="number"
            min={0}
            step="0.01"
            placeholder={t('vendors.amount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label className="w-40">
          <span className="sr-only">{t('vendors.dueDate')}</span>
          <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        <Button type="submit" variant="secondary" loading={add.isPending}>
          {t('vendors.addPayment')}
        </Button>
      </form>
    </div>
  );
}
